import React from "react";
import { ArrowRight, MapPin, Bed, Bath } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const properties = [{
  id: 1,
  title: "The Kensington Penthouse",
  price: "£4,500 pcm",
  location: "Kensington, London",
  beds: 3,
  baths: 2,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572105/img/prop_kensington.png",
  badge: "Premium",
  badgeVariant: "default"
}, {
  id: 2,
  title: "Mayfair Townhouse",
  price: "£12,000 pcm",
  location: "Mayfair, London",
  beds: 5,
  baths: 4,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572105/img/prop_mayfair.png",
  badge: "New",
  badgeVariant: "secondary"
}, {
  id: 3,
  title: "Chelsea Riverside Apartment",
  price: "£3,200 pcm",
  location: "Chelsea, London",
  beds: 2,
  baths: 2,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572105/img/prop_chelsea.png",
  badge: "Featured",
  badgeVariant: "default"
}, {
  id: 4,
  title: "Knightsbridge Garden Flat",
  price: "£5,500 pcm",
  location: "Knightsbridge, London",
  beds: 2,
  baths: 1,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572105/img/prop_knightsbridge.png",
  badge: "New",
  badgeVariant: "secondary"
}, {
  id: 5,
  title: "Notting Hill Studio",
  price: "£2,100 pcm",
  location: "Notting Hill, London",
  beds: 1,
  baths: 1,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572105/img/prop_notting_hill.png",
  badge: null,
  badgeVariant: null
}, {
  id: 6,
  title: "Hampstead Heath Villa",
  price: "£8,000 pcm",
  location: "Hampstead, London",
  beds: 4,
  baths: 3,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572105/img/prop_hampstead.png",
  badge: "Premium",
  badgeVariant: "default"
}];
export default function PremierRentalGallery() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="premier-rental-gallery" ref={ref} className={`py-24 bg-background transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif font-medium text-foreground mb-6">
            Premier Rental Gallery
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Discover our exclusive collection of high-end rental properties in
            London's most sought-after locations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {properties.map((property, i) => <div key={property.id} data-index={i} className="group">
              <Card className="bg-transparent border-none shadow-none overflow-hidden h-full flex flex-col">
                <div className="relative overflow-hidden rounded-xl mb-6 w-full">
                  <AspectRatio ratio={4 / 5}>
                    <img src={property.image} alt={property.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110" />
                  </AspectRatio>

                  {}
                  {property.badge && <div className="absolute top-4 left-4 z-20">
                      <Badge variant={property.badgeVariant as "default" | "secondary" | "destructive" | "outline"} className="uppercase tracking-wider font-sans text-xs px-3 py-1">
                        {property.badge}
                      </Badge>
                    </div>}

                  {}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-10 z-10">
                    <Button asChild className="bg-white text-black hover:bg-white/90 translate-y-4 group-hover:translate-y-0 transition-transform duration-300 font-sans uppercase tracking-wide">
                      <Link to="/PropertiesPage">
                        View Details
                      </Link>
                    </Button>
                  </div>
                </div>

                <CardContent className="px-2 py-0 flex-grow">
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-start">
                      <h3 className="text-2xl font-serif text-foreground leading-tight group-hover:text-secondary transition-colors duration-300">
                        {property.title}
                      </h3>
                    </div>
                    
                    <p className="text-xl font-sans font-bold text-secondary">
                      {property.price}
                    </p>

                    <div className="flex items-center gap-4 text-muted-foreground text-sm mt-2 font-sans">
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-4 h-4" />
                        <span>{property.location}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 text-muted-foreground text-sm mt-1 font-sans">
                       <div className="flex items-center gap-1.5">
                        <Bed className="w-4 h-4" />
                        <span>{property.beds} Beds</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Bath className="w-4 h-4" />
                        <span>{property.baths} Baths</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>

        <div className="mt-16 text-center">
          <Button asChild size="lg" variant="outline" className="border-foreground/20 hover:bg-foreground hover:text-background transition-colors font-sans uppercase tracking-widest">
            <Link to="/PropertiesPage">
              View All Properties <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}